import { submissionApiClient } from "@/api/submission-api";
import { workspaceApi, type Workspace } from "@/api/workspace-api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/store/auth-store";
import { isAxiosError } from "axios";
import { setPostHogGroup } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router";
import { ArrowRight, ArrowUpRight, SearchX } from "lucide-react";

import {
  CoverEyebrow,
  FundMark,
  PageContainer,
  fundCover,
  PublicCover,
  RaiseBoardNav,
  RaiseBoardShell,
} from "@/pages/raise-board/rb-ui";
import { IntakeStatus } from "@/components/portal/portal-ui";
import { ApplicationDeadlineCard } from "@/components/portal/application-deadline-card";
import { EligibilityCriteriaCard } from "@/components/portal/eligibility-criteria-card";
import { DescriptionCard } from "@/components/portal/description-card";
import { InvestmentDetailsCard } from "@/components/portal/investment-details-card";
import { InvestmentTeamCard } from "@/components/portal/investment-team-card";
import {
  firstSentence,
  formatCapitalRange,
  formatPortalDate,
  intakeState,
} from "@/components/portal/portal-utils";

export default function Portal() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const id = searchParams.get("id");
  const [data, setData] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<{
    hasApplied: boolean;
    applicationStatus: string | null;
    applicationId: string | null;
  } | null>(null);

  // The header's Apply button hands off to a compact one in the top bar once
  // it scrolls out of view, so exactly one is on screen at a time.
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const [heroCtaVisible, setHeroCtaVisible] = useState(true);

  useEffect(() => {
    const fetchOpportunity = async () => {
      if (!slug && !id) {
        setError("No opportunity ID or slug provided");
        setLoading(false);
        return;
      }

      try {
        const response = slug
          ? await workspaceApi.getPortalWorkspaceBySlug(slug)
          : await workspaceApi.getPortalWorkspace(id!);
        setData(response.result);

        if (response.result?._id) {
          setPostHogGroup("workspace", response.result._id, {
            name: response.result.opportunity_name,
          });
        }

        if (id && !slug && response.result.portal_slug) {
          const currentParams = new URLSearchParams(window.location.search);
          currentParams.delete("id");
          const queryString = currentParams.toString();
          const redirectUrl = `/p/${response.result.portal_slug}${queryString ? `?${queryString}` : ""}`;
          window.location.replace(redirectUrl);
          return;
        }

        const workspaceId = response.result._id;
        if (user && user.id && workspaceId) {
          try {
            const statusResponse =
              await submissionApiClient.application.checkApplicationStatus(
                workspaceId,
                user.id
              );
            setApplicationStatus({
              hasApplied: statusResponse.hasApplied,
              applicationStatus: statusResponse.applicationStatus,
              applicationId: statusResponse.applicationId,
            });
          } catch (err) {
            console.error("Error checking application status:", err);
            setApplicationStatus({
              hasApplied: false,
              applicationStatus: null,
              applicationId: null,
            });
          }
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError("Failed to load opportunity data");
        }
        console.error("Error fetching opportunity:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunity();
  }, [id, slug, user, navigate]);

  const getCompanyAvatar = () => {
    if (!data?.affiliated_company?.files) return null;
    const avatarFile = data.affiliated_company.files.find(
      (file) => file.file_type === "company avatar"
    );
    return avatarFile?.file_url || null;
  };

  const getButtonText = () => {
    if (!applicationStatus || !applicationStatus.hasApplied) return "Apply";
    if (applicationStatus.applicationStatus === "Portfolio Companies") return "Update";
    return "Edit";
  };

  const getButtonAction = () => {
    const workspaceId = data?._id || id;
    if (!applicationStatus || !applicationStatus.hasApplied) {
      return () => {
        if (slug) navigate(`/p/${slug}/submission`);
        else navigate(`/portal/submission?id=${workspaceId}`);
      };
    }
    return () => {
      if (slug) navigate(`/p/${slug}/submission?edit=true`);
      else navigate(`/portal/submission?id=${workspaceId}&edit=true`);
    };
  };

  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroCtaVisible(entry.isIntersecting),
      { rootMargin: "-56px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // The button only exists once loading has finished with data.
  }, [data, loading]);

  if (loading) {
    return (
      <RaiseBoardShell>
        <div role="status" aria-label="Loading portal">
          <div className="ink-scope bg-background">
            <div className="h-14 border-b border-border/60" />
            <PageContainer className="pb-20 pt-16 sm:pb-24 sm:pt-24">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <Skeleton className="mt-6 h-3.5 w-56" />
              <Skeleton className="mt-8 h-20 w-full max-w-xl rounded-xl" />
              <Skeleton className="mt-12 h-4 w-96 max-w-full" />
            </PageContainer>
          </div>
          <PageContainer className="space-y-3 pt-16">
            <Skeleton className="h-px w-full" />
            <Skeleton className="mt-6 h-6 w-32" />
            <Skeleton className="h-4 w-full max-w-[62ch]" />
            <Skeleton className="h-4 w-full max-w-[58ch]" />
            <Skeleton className="h-4 w-2/3" />
          </PageContainer>
        </div>
      </RaiseBoardShell>
    );
  }

  if (notFound || error || !data) {
    return (
      <RaiseBoardShell>
        <RaiseBoardNav onRequestAuth={() => navigate("/sign-in")} />
        <PublicCover
          size="lg"
          eyebrow={
            <>
              <SearchX />
              Applications
            </>
          }
          title="This portal isn't available"
          lede="The link may be mistyped, or the fund has closed this portal. Check the address with whoever shared it."
          aside={
            <Button asChild className="h-11 rounded-lg px-5">
              <a href="https://dgpt.io">Go to DiligenceGPT</a>
            </Button>
          }
        />
      </RaiseBoardShell>
    );
  }

  const primaryLocation =
    data.geographical_location && data.geographical_location.length > 0
      ? data.geographical_location[0]
      : undefined;
  const fundName = data.opportunity_name || "Application portal";
  const logoUrl = getCompanyAvatar();
  const cover = fundCover(data);
  const buttonText = getButtonText();
  const onApply = getButtonAction();
  const capital = formatCapitalRange(
    data.min_cheque_size,
    data.max_cheque_size,
    data.currency
  );
  const website = data.website_url?.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const state = intakeState(data.start_intake_date, data.end_intake_date);
  const opens = formatPortalDate(data.start_intake_date);
  const closes = formatPortalDate(data.end_intake_date);
  const lede =
    firstSentence(data.opportunity_description) ??
    ([capital && `Cheques of ${capital}`, primaryLocation && `for companies in ${primaryLocation}`]
      .filter(Boolean)
      .join(" ") ||
      `Apply to ${fundName}.`);

  // The date founders plan around, and what to call it.
  const deadline =
    state === "upcoming" && opens
      ? { label: "Opens", value: opens }
      : closes
        ? { label: state === "closed" ? "Closed" : "Closes", value: closes }
        : { label: "Deadline", value: "Rolling" };

  return (
    <RaiseBoardShell footerNote={`Applications go directly to ${fundName}.`}>
      <RaiseBoardNav
        onRequestAuth={() => navigate("/sign-in")}
        background={cover}
        brand={{
          name: fundName,
          logoUrl,
          context: "Applications",
        }}
        actions={
          <Button
            size="sm"
            onClick={onApply}
            tabIndex={heroCtaVisible ? -1 : 0}
            aria-hidden={heroCtaVisible}
            className={cn(
              "h-8 shrink-0 rounded-lg px-4 text-[13px] transition-[opacity,transform] duration-200 ease-out",
              heroCtaVisible
                ? "pointer-events-none translate-y-1 opacity-0"
                : "translate-y-0 opacity-100"
            )}
          >
            {buttonText}
          </Button>
        }
      />

      <PublicCover
        background={cover}
        eyebrow={
          <>
            <div className="mb-5 basis-full">
              <FundMark
                name={fundName}
                logoUrl={logoUrl}
                size="lg"
                className="border-transparent bg-[hsl(220_30%_96%)] text-[hsl(226_34%_8%)]"
              />
            </div>
            <CoverEyebrow
              items={[
                data.opportunity_type,
                primaryLocation,
                <IntakeStatus
                  key="status"
                  state={state}
                  tone="ink"
                  className="text-[12px] tracking-[0.18em]"
                />,
              ]}
            />
          </>
        }
        title={fundName}
        lede={lede}
        aside={
          <>
            <div className="sm:text-right">
              <p className="text-[12px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                {deadline.label}
              </p>
              <p className="mt-1.5 text-[clamp(1.6rem,1.3rem+1vw,2.1rem)] font-semibold leading-none tabular-nums tracking-[-0.03em]">
                {deadline.value}
              </p>
            </div>
            <div ref={heroCtaRef} className="flex flex-wrap items-center gap-4 sm:justify-end">
              {website && (
                <a
                  href={data.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[14px] text-muted-foreground underline-offset-4 transition-colors duration-150 hover:text-foreground hover:underline"
                >
                  {website}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              )}
              <Button onClick={onApply} className="h-11 gap-2 rounded-lg px-6 text-[15px]">
                {buttonText}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </>
        }
      />

      <PageContainer className="space-y-16 pt-16 sm:space-y-20 sm:pt-20">
        <DescriptionCard description={data.opportunity_description} />
        <ApplicationDeadlineCard
          startDate={data.start_intake_date}
          endDate={data.end_intake_date}
          capital={capital}
          type={data.opportunity_type}
          locations={data.geographical_location}
        />
        <EligibilityCriteriaCard
          gicsIndustries={data.target_gics_industries}
          gicsSubIndustries={data.target_gics_sub_industries}
          revenueModels={data.target_revenue_models}
          stages={data.target_stage_companies}
          locations={data.geographical_location}
          businessModels={data.target_model_companies}
          distinctions={data.target_distinctions}
        />
        <InvestmentDetailsCard prize={data.prize} />
        {data.show_judge_on_opportunity && (
          <InvestmentTeamCard teamMembers={data.team_members} />
        )}
      </PageContainer>
    </RaiseBoardShell>
  );
}
